import { apiFetch } from "../api.js";
import type {
  Bill,
  BillsListResponse,
  CreateBillInput,
  UpdateBillInput,
} from "./types.js";

/**
 * Bills API module — Phase 5 Session 2.
 *
 * - `markBillPaid()` is the convenience wrapper around the server-side
 *   mark-paid shortcut (`PATCH /v1/bills/{id}` with `{ mark_paid: true }`).
 *   The server reads the bill's recurrence + start_date, computes the
 *   next due_date, and (for `one_time`) archives the row.
 * - `deleteBill()` is a soft-delete (server sets `is_archived = true`).
 *   Idempotent: 204 on already-archived rows.
 */

export function listBills(
  includeArchived = false,
): Promise<BillsListResponse> {
  const qs = includeArchived ? "?include_archived=true" : "";
  return apiFetch<BillsListResponse>(`/v1/bills${qs}`);
}

export function getBill(billId: string): Promise<Bill> {
  return apiFetch<Bill>(`/v1/bills/${billId}`);
}

export function createBill(input: CreateBillInput): Promise<Bill> {
  return apiFetch<Bill>("/v1/bills", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateBill(
  billId: string,
  input: UpdateBillInput,
): Promise<Bill> {
  return apiFetch<Bill>(`/v1/bills/${billId}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function markBillPaid(billId: string): Promise<Bill> {
  return updateBill(billId, { mark_paid: true });
}

export function deleteBill(billId: string): Promise<void> {
  return apiFetch<void>(`/v1/bills/${billId}`, { method: "DELETE" });
}
